import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Grid, IconButton, Stack, TextField, Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../services/api';

// Tender items (OCDS tender.items), shared by CreateTender and EditTender.
//
// An item is what is being procured. For works it is the work itself, not a
// line of the bill of quantities, which is published as a tender document.

const DOT = '\u00b7';

const LEVEL_LABEL = {
  SEGMENT: 'Segment',
  FAMILY: 'Family',
  CLASS: 'Class',
  COMMODITY: 'Commodity',
};

let keySeq = 0;
function newKey() {
  keySeq += 1;
  return `new-${keySeq}`;
}

export function emptyItem() {
  return {
    key: newKey(),
    id: undefined,
    description: '',
    quantity: '',
    unitName: '',
    unitValue: '',
    classification: null,
  };
}

// Server items -> editable rows.
export function itemsFromTender(items) {
  return (items || []).map((i) => ({
    key: i.id,
    id: i.id,
    description: i.description || '',
    quantity: i.quantity ?? '',
    unitName: i.unitName || '',
    unitValue: i.unitValue ?? '',
    classification: i.classification
      ? { code: i.classification.code, title: i.classification.title, level: i.classification.level }
      : null,
  }));
}

function isBlank(i) {
  return !String(i.description).trim()
    && !i.classification
    && String(i.quantity) === ''
    && !String(i.unitName).trim()
    && String(i.unitValue) === '';
}

// Editable rows -> request body. A completely empty row is dropped; a row with
// anything in it is sent, so the server can say what is missing instead of
// the row silently disappearing.
export function itemsToPayload(items) {
  return items
    .filter((i) => !isBlank(i))
    .map((i) => ({
      ...(i.id ? { id: i.id } : {}),
      description: String(i.description).trim(),
      classificationId: i.classification ? i.classification.code : null,
      quantity: String(i.quantity) === '' ? null : i.quantity,
      unitName: String(i.unitName).trim() || null,
      unitValue: String(i.unitValue) === '' ? null : i.unitValue,
    }));
}

function optionLabel(o) {
  return o ? `${o.code} ${DOT} ${o.title}` : '';
}

function UnspscPicker({ value, onChange, disabled }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const term = input.trim();
    // Nothing to search, or the box just shows the chosen code.
    if (term.length < 2 || (value && input === optionLabel(value))) {
      latest.current += 1;
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    latest.current += 1;
    const req = latest.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/meta/unspsc', { params: { q: term, limit: 20 } });
        if (req === latest.current) setOptions(data.data || []);
      } catch (err) {
        if (req === latest.current) setOptions([]);
      } finally {
        if (req === latest.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, value]);

  // Keep the chosen code among the options so the box can display it.
  const shown = value && !options.some((o) => o.code === value.code)
    ? [value, ...options]
    : options;

  const tooShort = input.trim().length < 2;

  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v ? { code: v.code, title: v.title, level: v.level } : null)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      options={shown}
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(o, v) => o.code === v.code}
      loading={loading}
      disabled={disabled}
      noOptionsText={tooShort ? 'Type at least 2 letters, or the start of a code' : 'No matching codes'}
      renderOption={(props, o) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Box>
              <Typography variant="body2">{o.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {`${o.code} ${DOT} ${LEVEL_LABEL[o.level] || o.level || ''}`}
                {o.parentTitle ? ` ${DOT} ${o.parentTitle}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="UNSPSC code"
          placeholder="Search, e.g. classroom chairs"
          helperText="Optional. Search by what is being bought, or type the start of a code."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function TenderItemsEditor({ items, onChange, disabled = false, currency = 'NGN' }) {
  const update = (i, patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const setField = (i, k) => (e) => update(i, { [k]: e.target.value });
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, emptyItem()]);
  const move = (i, step) => {
    const j = i + step;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        List what is being procured. For works, an item is the work itself (for example,
        construction of a two-classroom block at a named school), not each line of the bill of
        quantities; the BOQ goes under Tender documents. Items are optional.
      </Typography>

      {disabled && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fixed: these are the items bidders saw when the tender was published.
        </Typography>
      )}

      {items.length === 0 && (
        <Typography variant="body2" sx={{ mb: 2 }}>No items listed.</Typography>
      )}

      {items.map((it, i) => (
        <Box key={it.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2">Item {i + 1}</Typography>
            {!disabled && (
              <Stack direction="row">
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton size="small" onClick={() => move(i, 1)} disabled={i === items.length - 1}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove item">
                  <IconButton size="small" onClick={() => remove(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Description" value={it.description} onChange={setField(i, 'description')}
                fullWidth required multiline disabled={disabled}
                placeholder="e.g. Construction of a two-classroom block" />
            </Grid>
            <Grid item xs={12} md={6}>
              <UnspscPicker value={it.classification} disabled={disabled}
                onChange={(v) => update(i, { classification: v })} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField label="Quantity" type="number" value={it.quantity} onChange={setField(i, 'quantity')}
                fullWidth disabled={disabled} inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField label="Unit" value={it.unitName} onChange={setField(i, 'unitName')}
                fullWidth disabled={disabled} placeholder="e.g. blocks, sets, units" />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField label={`Unit value (${currency || 'NGN'})`} type="number" value={it.unitValue}
                onChange={setField(i, 'unitValue')} fullWidth disabled={disabled} inputProps={{ min: 0 }} />
            </Grid>
          </Grid>
        </Box>
      ))}

      {!disabled && <Button onClick={add}>+ Add item</Button>}
    </>
  );
}